import { ForwardedRef, ReactNode, createContext, forwardRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { animated, useSpring } from "react-spring";

import { Button, Buttons, IconSize } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { BoundsObserver } from "capitalrx-components-2.0/lib/utilities";
import classNames from "classnames";

import { ActionBars, AgentChatbotFlyout, AgentChatContextProvider, BreadcrumbItems, BreadcrumbRoot, ContentChannel, MenuBar, TaskFlyout, useContentChannel } from "./components";

import styles from "./ContentLayout.module.css";

/** The set of available layout types.  */
export type LayoutType = "auto" | "auto-unpadded" | "fixed" | "fixed-unpadded" | "fullscreen";

/** A no-operation function. */
function noOp(): void {
  /** No operation */
}

/** Content bounds. */
interface Bounds {

  /** The top distance in pixels. */
  readonly top: number;

  /** The left distance in pixels. */
  readonly left: number;

  /** The right distance in pixels. */
  readonly right: number;

  /** The width in pixels. */
  readonly width: number;

  /** The bottom distance in pixels. */
  readonly bottom: number;
}

/** The `ContentLayout` component properties. */
export interface ContentLayoutProps {

  /** Indicates whether the content should be displayed fullscreen. */
  readonly fullscreen?: boolean;

  /** The component children. */
  readonly children?: ReactNode;
}

/** The layout context model. */
interface ContentLayoutContextValue {

  /** The current content layout type. */
  readonly layoutType: LayoutType;

  /** Sets the layout type. */
  readonly setLayoutType: (layout: LayoutType) => void;
}

/** The content layout context. */
const ContentLayoutContext = createContext<ContentLayoutContextValue>({
  layoutType: "auto",
  setLayoutType: noOp,
});

/** Returns back a function that can be called to set the current layout. */
export function useSetLayout(): (layout: LayoutType) => void {
  const { setLayoutType } = useContext(ContentLayoutContext);

  // If the component performing layout changes unmounts, reset back to "auto".
  useEffect(() => {
    return () => {
      setLayoutType("auto");
    };
  }, []);

  return useMemo(() => setLayoutType, [setLayoutType]);
}

/**
 * Gets the current layout.
 * @returns The current layout. */
export function useLayout(): LayoutType {
  const { layoutType } = useContext(ContentLayoutContext);
  return layoutType;
}

/** The content layout. */
export const ContentLayout = (props: ContentLayoutProps): ReactNode => {
  const { fullscreen = false, children } = props;

  const location = useLocation();
  const [layoutType, setLayoutType] = useState<LayoutType>("auto");

  useEffect(() => {
    if (fullscreen) {
      setLayoutType((current) => {
        if (!current.startsWith("fullscreen")) return "fullscreen";
        return current;
      });
    }

    return () => {
      setLayoutType("auto");
    };
  }, [fullscreen]);

  const contextValue = useMemo<ContentLayoutContextValue>(
    () => ({
      layoutType,
      setLayoutType,
    }),
    [layoutType],
  );

  return (
    <div className={classNames("content-layout", styles.root, { [styles.nonFullscreen]: !layoutType.startsWith("fullscreen") })}>
      <ContentLayoutContext.Provider value={contextValue}>
        <BreadcrumbRoot>
          <MenuBar />
          <MainContentContainer key={location.pathname}>
            <ActionBars />
            <LayoutContainer>{children}</LayoutContainer>
          </MainContentContainer>
        </BreadcrumbRoot>
      </ContentLayoutContext.Provider>
    </div>
  );
};

/** The `MainContentContainer` component properties. */
interface MainContentContainerProps {

  /** The component children. */
  readonly children?: ReactNode;
}

/** The main content container. */
const MainContentContainer = (props: MainContentContainerProps): ReactNode => {
  const { children } = props;

  const animStyles = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    delay: 50,
    config: { duration: 500 },
  });

  return (
    <animated.div className="main-content-container" style={animStyles}>
      {children}
    </animated.div>
  );
};

/** The `SideBarContainer` component properties. */
interface SideBarContainerProps {

  /** The sidebar ID. */
  readonly id?: string;

  /** The sidebar type. */
  readonly type: "left" | "right";

  /** The sidebar index. */
  readonly index: number;

  /** The content bounds. */
  readonly bounds: Bounds;

  /** The component children. */
  readonly children?: ReactNode;
}

const ExpandContractButtonAppearance = {
  ...Buttons.Functional.Tertiary.Large,
  padding: {
    horizontal: 4,
    vertical: 4,
  },
  icon: {
    size: "regular" as IconSize,
  },
};

/** The component that wraps around the sidebar elements. */
const SideBarContainer = (props: SideBarContainerProps): ReactNode => {
  const { id, type, bounds, children } = props;
  const { top } = bounds;

  const [expanded, setExpanded] = useState(true);
  const childrenContainerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number | null>(null);

  const [resizeObserver] = useState(() => new ResizeObserver((entries) => {
    const entry = entries[0];
    if (!entry) return;
    if (!entry.target?.firstChild?.hasChildNodes?.()) return;

    setWidth(entry.contentRect.width);
  }));

  useEffect(() => {
    const storageKey = `sb_${id}`;
    setExpanded((localStorage.getItem(storageKey) ?? "true") === "true");
  }, [id]);

  const style = useMemo(() => ({ top, height: `calc(100vh - ${top}px)` }), [top]);
  const scrollAreaStyle = useMemo(() => {
    if (width === null) return {};
    return (expanded) ? { width: width + 48 } : { width: 44 };
  }, [expanded, width]);

  useEffect(() => {
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const toggleHandler = useCallback(() => {
    setExpanded((x) => {
      const result = !x;
      const storageKey = `sb_${id}`;
      localStorage.setItem(storageKey, (result) ? "true" : "false");
      return result;
    });
  }, [id]);

  const childrenRefHandler = useCallback((ref: HTMLDivElement | null) => {
    if (childrenContainerRef.current !== ref && childrenContainerRef.current) {
      resizeObserver.unobserve(childrenContainerRef.current);
      childrenContainerRef.current = null;
    }

    if (ref) resizeObserver.observe(ref);
    childrenContainerRef.current = ref;
  }, []);

  return (
    <Stack className={classNames("sidebar-container", type, { expanded: expanded, contracted: !expanded })} style={style}>
      <Shelf reverse={type === "right"}>
        <Shelf.Spacer />
        <Button appearance={ExpandContractButtonAppearance} icon={(expanded) ? "x" : "menu-bars"} onClick={toggleHandler} />
      </Shelf>
      <div className="scroll-area" style={scrollAreaStyle}>
        <div ref={childrenRefHandler} className="children">
          {children}
        </div>
      </div>
    </Stack>
  );
};

/** The `BodyContainer` component properties. */
interface BodyContainerProps {

  /** The current bounds. */
  readonly bounds: Bounds;

  /** The component children. */
  readonly children?: ReactNode;
}

/** The layout body container. */
const BodyContainer = forwardRef((props: BodyContainerProps, ref: ForwardedRef<HTMLDivElement>): ReactNode => {
  const { bounds, children } = props;

  const actionBars = useContentChannel(null, "app-action-bars");
  const { layoutType } = useContext(ContentLayoutContext);

  const [tasksVisible, setTasksVisible] = useState(false);

  const className = useMemo(() => {
    const width = bounds.width - 80; // Let's just take into account the body padding.

    if (width <= 320) return "mobile";
    else if (width <= 576) return "mobile-landscape";
    else if (width <= 768) return "tablet";
    else if (width <= 992) return "laptop";
    else if (width <= 1024) return "desktop-narrow";
    else if (width <= 1200) return "desktop";
    else return "desktop-wide";
  }, [layoutType, bounds.width]);

  useEffect(() => {
    if (tasksVisible) document.body.classList.add("has-tasks");
    else document.body.classList.remove("has-tasks");
  }, [tasksVisible]);

  let layoutClassName = "auto";
  if (layoutType.startsWith("fixed")) {
    layoutClassName = "fixed";
  } else if (layoutType.startsWith("auto")) {
    layoutClassName = "auto";
  }

  return (
    <AgentChatContextProvider>
      <TaskFlyout onVisibilityChange={setTasksVisible} />
      <AgentChatbotFlyout hasTasks={tasksVisible} />
      <div ref={ref} className={classNames("body-container", { "unpadded": layoutType.endsWith("unpadded") })}>
        <div className={classNames("body-layout", layoutClassName, className)}>
          {actionBars.length === 0 && <BreadcrumbItems />}
          {children}
        </div>
      </div>
    </AgentChatContextProvider>
  );
});

/** The `BodyShadow` component properties. */
interface BodyShadowProps {

  /** The content bounds. */
  readonly bounds: Bounds;
}

/** The distance to shift the edges of the shadow. */
const ShiftDistance = -64;

/** Casts a shadow on the body based on the layout configuration. */
const BodyShadow = (props: BodyShadowProps): ReactNode => {
  const { bounds } = props;
  const { layoutType } = useContext(ContentLayoutContext);

  const styles = useMemo(() => {
    const { top, left, right, bottom } = bounds;

    return {
      top,
      left: left === 0 ? ShiftDistance : left,
      right: right === 0 ? ShiftDistance : right,
      bottom: bottom === 0 ? ShiftDistance : bottom,
    };
  }, [bounds]);

  if (layoutType.startsWith("fullscreen") || layoutType.endsWith("unpadded")) {
    return null;
  }
  return <div className="body-shadow" style={styles} />;
};

/** The `LayoutContainer` component properties. */
interface LayoutContainerProps {

  /** The component children. */
  readonly children?: ReactNode;
}

const LayoutContainer = (props: LayoutContainerProps): ReactNode => {
  const { children } = props;

  const [currentBounds, setCurrentBounds] = useState<Bounds>({ top: 0, left: 0, right: 0, bottom: 0, width: 0 });

  const boundsObserver = useMemo(
    () =>
      new BoundsObserver((entries) => {
        const [entry] = entries;
        const { target } = entry;
        const { offsetParent } = target;

        const parentWidth = offsetParent === null ? window.innerWidth : (offsetParent as HTMLElement).offsetWidth;
        const parentHeight = offsetParent === null ? window.innerHeight : (offsetParent as HTMLElement).offsetHeight;

        setCurrentBounds((previousBounds) => {
          const { top, left, right, bottom, width } = previousBounds;
          const newTop = entry.offsetTop;
          const newLeft = entry.offsetLeft;
          const newRight = parentWidth - entry.bounds.width - entry.offsetLeft;
          const newBottom = parentHeight - entry.bounds.height - entry.offsetTop;
          const newWidth = parentWidth - (newLeft + newRight);

          if (top !== newTop || left !== newLeft || right !== newRight || bottom !== newBottom || width !== newWidth) {
            return {
              top: newTop,
              left: newLeft,
              right: newRight,
              bottom: newBottom,
              width: newWidth,
            };
          }

          return previousBounds;
        });
      }),
    [],
  );

  const containerRefHandler = useCallback(
    (ref: HTMLDivElement | null) => {
      if (ref) boundsObserver.observe(ref);
      else boundsObserver.disconnect();
    },
    [boundsObserver],
  );

  return (
    <div className="layout-container">
      <LeftSideBars bounds={currentBounds} />
      <BodyContainer ref={containerRefHandler} bounds={currentBounds}>
        {children}
      </BodyContainer>
      <RightSideBars bounds={currentBounds} />
      <BodyShadow bounds={currentBounds} />
    </div>
  );
};

/** The params passed to sidebar channels. */
interface SideBarParams {

  /** The sidebar ID. */
  readonly id?: string;
}

/**  The `LeftSideBars` component properties. */
interface LeftSidebarsProps {

  /** The content bounds. */
  readonly bounds: Bounds;
}

/** The layout left sidebars. */
const LeftSideBars = (props: LeftSidebarsProps): ReactNode => {
  const { bounds } = props;
  const { layoutType } = useContext(ContentLayoutContext);

  if (layoutType.startsWith("fullscreen")) {
    return null;
  }

  return (
    <ContentChannel id="app-sidebar-left">
      {(children, index, _, __, params) => {
        let sideBarParams: SideBarParams | undefined = undefined;

        if (typeof params === "object" && params && "id" in params)
          sideBarParams = params as SideBarParams;

        return (<SideBarContainer id={sideBarParams?.id ?? `left_${index}`} type="left" index={index} bounds={bounds}>{children}</SideBarContainer>);
      }}
    </ContentChannel>
  );
};

/** The `RightSideBars` component properties. */
interface RightSideBarsProps {

  /** The content bounds. */
  readonly bounds: Bounds;
}

/** The layout right sidebars. */
const RightSideBars = (props: RightSideBarsProps): ReactNode => {
  const { bounds } = props;
  const { layoutType } = useContext(ContentLayoutContext);

  if (layoutType.startsWith("fullscreen")) {
    return null;
  }
  return (
    <ContentChannel id="app-sidebar-right">
      {(children, index, _, __, params) => {
        let sideBarParams: SideBarParams | undefined = undefined;

        if (typeof params === "object" && params && "id" in params)
          sideBarParams = params as SideBarParams;

        return (<SideBarContainer id={sideBarParams?.id ?? `right_${index}`} type="right" index={index} bounds={bounds}>{children}</SideBarContainer>);
      }}
    </ContentChannel>
  );
};
